import { useState } from 'react';
import { Link } from 'react-router';
import { api, type FeatureRequestListing } from '../../api/client';
import { Button, Card, Chip, Dialog, Empty, LoadError, Loading, Notice, PageHeader, TextAreaField } from '../../components/ui';
import { shortWhen } from '../../lib/format';
import { useData } from '../../lib/useData';
import { FEATURE_STATE } from './FeaturesCard';

/**
 * Stitch Admin → Requests (1.13.0, Teampace 3000): features companies' admins asked Stitch to turn
 * on. Turn on does it and answers the request (the asker is told); a feature blocked by something
 * Stitch sets up links to where it's fixed; Close answers it with a reason the company sees.
 */
export function AdminRequests() {
  const [status, setStatus] = useState<'open' | 'all'>('open');
  const data = useData(() => api.featureRequests(status), [status]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [closing, setClosing] = useState<FeatureRequestListing | null>(null);
  const [reason, setReason] = useState('');

  const turnOn = async (r: FeatureRequestListing) => {
    setBusy(r.id);
    setError('');
    setNote('');
    try {
      const out = await api.turnOnFeatureRequest(r.id);
      setNote(out.answered ? `${r.featureName} is on for ${r.customerName}, and ${r.askedByName || 'whoever asked'} has been told.` : `${r.featureName} is being turned on for ${r.customerName}. Answer the request once it’s on.`);
      data.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy('');
    }
  };

  const close = async () => {
    if (!closing) return;
    setBusy(closing.id);
    setError('');
    try {
      await api.closeFeatureRequest(closing.id, reason);
      setNote(`Answered: ${closing.customerName} sees why.`);
      setClosing(null);
      setReason('');
      data.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy('');
    }
  };

  const action = (r: FeatureRequestListing) => {
    if (r.status !== 'open') return null;
    const c = r.now?.control;
    const going = busy === r.id;
    const turnable = r.now?.state === 'on' || (r.now?.state === 'off' && (c?.type === 'button' || (c?.type === 'switch' && !c.on)));
    return (
      <span className="row-actions">
        {turnable ? (
          <Button size="small" variant="primary" disabled={going} onClick={() => void turnOn(r)}>
            {going ? 'Working…' : r.now?.state === 'on' ? 'It’s on: tell them' : 'Turn on'}
          </Button>
        ) : c?.type === 'link' ? (
          <Link className="btn small secondary" to={c.to.startsWith('#') ? `/admin/customers/${encodeURIComponent(r.customerId)}${c.to}` : c.to}>
            {c.label}
          </Link>
        ) : null}
        <Button size="small" variant="ghost" disabled={going} onClick={() => setClosing(r)}>
          Close…
        </Button>
      </span>
    );
  };

  const list = data.data?.requests ?? [];
  return (
    <>
      <PageHeader title="Requests" />
      <div className="content">
        <p className="muted page-lede">
          Features a company’s admins asked Stitch to turn on, from What’s on in their Settings. Turning one on answers the request, and whoever asked is told. Close one
          with a reason when it isn’t going to be on yet: the company sees why.
        </p>
        {error && <Notice tone="crit">{error}</Notice>}
        {note && <Notice tone="ok">{note}</Notice>}
        <Card
          title={status === 'open' ? 'Waiting for an answer' : 'Every request'}
          action={
            <span className="row-actions">
              <Button size="small" variant={status === 'open' ? 'secondary' : 'ghost'} aria-pressed={status === 'open'} onClick={() => setStatus('open')}>
                Open
              </Button>
              <Button size="small" variant={status === 'all' ? 'secondary' : 'ghost'} aria-pressed={status === 'all'} onClick={() => setStatus('all')}>
                All
              </Button>
            </span>
          }
        >
          {data.loading && !data.data ? (
            <Loading />
          ) : data.error ? (
            <LoadError error={data.error} retry={data.reload} />
          ) : !list.length ? (
            <Empty title={status === 'open' ? 'Nothing waiting' : 'No requests yet'}>Companies’ admins ask from What’s on, in their Settings.</Empty>
          ) : (
            <ul className="features" aria-label="Requests">
              {list.map((r) => (
                <li key={r.id} className={`feature ${r.now?.state ?? ''}`}>
                  <div className="feature-main">
                    <div className="feature-title">
                      <strong>{r.featureName}</strong>
                      <span>
                        for <Link to={`/admin/customers/${encodeURIComponent(r.customerId)}#features`}>{r.customerName}</Link>
                      </span>
                      {r.status === 'open' && r.now && (
                        <Chip small tone={FEATURE_STATE[r.now.state].tone}>
                          {`${FEATURE_STATE[r.now.state].label} now`}
                        </Chip>
                      )}
                      {r.status !== 'open' && <Chip small tone={r.status === 'done' ? 'ok' : 'neutral'}>{r.status === 'done' ? 'Turned on' : 'Closed'}</Chip>}
                    </div>
                    <span className="muted">
                      Asked by {r.askedByName || 'someone'} · {shortWhen(r.askedAt)}
                      {r.answeredAt ? ` · answered by ${r.answeredByName || 'Stitch'} ${shortWhen(r.answeredAt)}` : ''}
                    </span>
                    {r.status === 'open' && r.now?.reason && <span className="feature-reason">{r.now.reason}</span>}
                    {r.status === 'closed' && r.reason && <span className="feature-reason">“{r.reason}”</span>}
                  </div>
                  <div className="feature-control">{action(r)}</div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
      <Dialog
        open={closing !== null}
        onClose={() => setClosing(null)}
        title={closing ? `Close ${closing.customerName}’s request` : ''}
        subtitle={closing ? `For ${closing.featureName}. ${closing.askedByName || 'Whoever asked'} is told, with your reason.` : ''}
        foot={
          <>
            <Button variant="ghost" onClick={() => setClosing(null)}>
              Cancel
            </Button>
            <Button variant="primary" disabled={!reason.trim() || busy !== ''} onClick={() => void close()}>
              Close the request
            </Button>
          </>
        }
      >
        <TextAreaField label="Why it isn’t on yet" hint="A sentence or two, in plain words: the company sees it." rows={3} maxLength={500} value={reason} data-autofocus onChange={(e) => setReason(e.target.value)} />
      </Dialog>
    </>
  );
}
